function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* eslint-disable require-jsdoc */
import React from 'react';
import { NumericFormat } from 'react-number-format';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import fonts from '../../theme/fonts.js';
import { useTheme } from '@mui/system';
import { colors } from '../../theme/index.js';
const StyledTextField = styled(TextField)(({
  theme
}) => ({
  '& .MuiInputBase-root': {
    fontFamily: '"AvenirLTPro-Book", sans-serif',
    fontWeight: 400,
    color: theme.palette.default.contrastText,
    // this keeps letter spacing consistent with site before mui5 upgrade
    letterSpacing: "0.14994px"
  },
  "& .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-error):before": {
    borderColor: theme.palette.custom.textFieldHover
  },
  "& .MuiInput-underline:hover:not(.Mui-disabled):not(.Mui-error):before": {
    borderColor: theme.palette.custom.textFieldHover,
    borderBottomWidth: "2px"
  }
}));

/**
 *
 * @param {*} props
 */
const NumberFormatCustom = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    allowleadingzeros,
    inputRef,
    onChange,
    onBlur,
    ...other
  } = props;
  return /*#__PURE__*/React.createElement(NumericFormat, _extends({}, other, {
    getInputRef: ref,
    onChange: e => e.stopPropagation(),
    onValueChange: values => {
      onChange({
        target: {
          value: values.value
        }
      });
    },
    onBlur: e => {
      onBlur({
        target: {
          value: Number(e.target.value.replace('$', '').replace('%', '').replaceAll(',', '').trim())
        }
      });
    },
    allowNegative: false,
    allowLeadingZeros: Boolean(allowleadingzeros)
  }));
});
NumberFormatCustom.displayName = 'NumberFormatCustom';

/**
 * 
 * @param {*} value 
 * @param {*} max 
 * @returns 
 */
const isValueAllowed = (value, max) => {
  // If max is not explicitly set to 'false', check if the value is within the maximum allowed limit
  if (max !== 'false') {
    return value === undefined || value <= max;
  }

  // If max is 'false', allow any value
  return true;
};

/**
 *
 * @param {*} props
 */
const NumberFormatCurrency = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    allowleadingzeros = false,
    allowNegative,
    inputRef,
    onChange,
    onBlur,
    decimalscale,
    max = 'false',
    ...other
  } = props;
  return /*#__PURE__*/React.createElement(NumericFormat, _extends({}, other, {
    ref: ref,
    isAllowed: values => {
      const {
        value
      } = values;
      return isValueAllowed(value, max);
    }

    // the following two props are used to save values
    ,
    onChange: e => e.stopPropagation(),
    onBlur: e => {
      onBlur({
        target: {
          value: Number(e.target.value.replace('$', '').replace('%', '').replaceAll(',', '').trim())
        }
      });
    }

    // this prop is used to set the internal value of the component, and interacts with the label component etc
    ,
    onValueChange: values => {
      onChange({
        target: {
          value: values.value
        }
      });
    },
    thousandSeparator: true,
    prefix: "$",
    decimalScale: decimalscale || 2,
    fixedDecimalScale: true,
    allowNegative: allowNegative,
    allowLeadingZeros: Boolean(allowleadingzeros)
  }));
});
NumberFormatCurrency.displayName = 'NumberFormatCurrency';

/**
 *
 * @param {*} props
 * todo: make this work
 */
const NumberFormatPercentage = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    allowLeadingZeros,
    inputRef,
    onChange,
    onBlur,
    decimalscale,
    limitonehundred,
    ...other
  } = props;
  const MAX_LIMIT = 100;
  return /*#__PURE__*/React.createElement(NumericFormat, _extends({}, other, {
    getInputRef: inputRef,
    isAllowed: values => {
      const {
        value
      } = values;
      // this will limit the value to 100, and will still allow the user to enter in 0
      return value === undefined || value <= MAX_LIMIT;
    },
    onChange: e => e.stopPropagation(),
    onValueChange: values => {
      onChange({
        target: {
          value: values.value >= 100 && limitonehundred === "true" ? 100 : values.value
        }
      });
    },
    onBlur: e => {
      onBlur({
        target: {
          value: Number(e.target.value.replace('$', '').replace('%', '').replaceAll(',', '').trim())
        }
      });
    },
    suffix: "%",
    decimalScale: decimalscale || 2,
    fixedDecimalScale: true,
    allowNegative: false,
    allowLeadingZeros: false
  }));
});
NumberFormatPercentage.displayName = 'NumberFormatPercentage';

/**
 *
 * @param {*} props
 */
const NumberInput = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    id,
    testid,
    select,
    error,
    helperText,
    customHelperTextProps,
    label,
    value,
    autoFocus,
    margin = 'normal',
    min,
    onChange,
    onKeyPress,
    onBlur,
    fullWidth,
    autoComplete = 'off',
    allowLeadingZeros,
    allowNegative,
    required,
    variant,
    disabled,
    prefix,
    suffix,
    decimalPlaces,
    limitonehundred,
    max = 'false'
  } = props;
  const theme = useTheme();
  let NumberFormatType = NumberFormatCustom;
  if (prefix) {
    NumberFormatType = NumberFormatCurrency;
  }
  if (suffix) {
    NumberFormatType = NumberFormatPercentage;
  }
  const style = {
    backgroundColor: props.disabled ? '#f7f7f7' : null,
    // this sets the outline of the border when hovered
    '& .MuiOutlinedInput-root': {
      "&:hover fieldset": {
        // sets the border color of the outlined variant only if not disabled and no error present
        borderColor: !props.error && !props.disabled && theme.palette.custom.textFieldHover
      }
    },
    "& .MuiInputBase-input.Mui-disabled": {
      // sets the color of the disabled field text
      WebkitTextFillColor: colors.mainTextGrey
    },
    ...(props.customStyle || {})
  };
  return /*#__PURE__*/React.createElement(StyledTextField, {
    id: id || testid,
    select: select,
    ref: ref,
    error: Boolean(error),
    helperText: helperText || error,
    FormHelperTextProps: {
      style: {
        fontFamily: fonts.type.base,
        ...customHelperTextProps
      }
    },
    label: label,
    value: value,
    autoFocus: autoFocus,
    InputLabelProps: {
      style: {
        fontFamily: fonts.type.light,
        color: theme.primary
      }
    },
    margin: margin // none, dense, or normal
    ,
    min: min,
    onChange: onChange,
    onKeyPress: onKeyPress,
    onBlur: onBlur,
    fullWidth: fullWidth,
    InputProps: {
      autoComplete: autoComplete,
      inputComponent: NumberFormatType
    },
    sx: style,
    inputProps: {
      allowleadingzeros: allowLeadingZeros || "false",
      decimalscale: decimalPlaces || 2,
      limitonehundred: limitonehundred || "true",
      allowNegative: allowNegative || false,
      max: max ? max : undefined
    },
    required: required,
    variant: variant || 'outlined',
    disabled: disabled
  });
});
NumberInput.displayName = 'NumberInput';
export default NumberInput;